import { GridRowId } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';

interface SelectionState<TRow> {
  ids: GridRowId[];
  rowsById: Map<GridRowId, TRow>;
}

export interface UseGridSelectionReturn<TRow> {
  selectedIds: GridRowId[];
  handleRowSelectionChange: (newSelection: GridRowId[]) => void;
  clearAllSelections: () => void;
  selectedRows: TRow[];
  visibleSelectedIds: GridRowId[];
}

export const useGridSelection = <TRow, TFilter extends object>(
  rows: TRow[],
  getRowId: (row: TRow) => GridRowId,
  filterParams: TFilter,
): UseGridSelectionReturn<TRow> => {
  const [selectionState, setSelectionState] = useState<SelectionState<TRow>>({
    ids: [],
    rowsById: new Map(),
  });

  const handleRowSelectionChange = useCallback(
    (newSelection: GridRowId[]): void => {
      setSelectionState(prev => {
        const currentPageIds = new Set<GridRowId>(rows.map(getRowId));
        const otherPageSelections = prev.ids.filter(
          id => !currentPageIds.has(id),
        );
        const rowDataMap = new Map<GridRowId, TRow>(
          rows.map(row => [getRowId(row), row]),
        );

        return {
          ids: [...otherPageSelections, ...newSelection],
          rowsById: new Map([
            ...Array.from(prev.rowsById.entries()).filter(
              ([id]) => !currentPageIds.has(id),
            ),
            ...newSelection
              .map(id => [id, rowDataMap.get(id)] as const)
              .filter(([, row]) => row !== undefined),
          ] as Array<readonly [GridRowId, TRow]>),
        };
      });
    },
    [rows, getRowId],
  );

  const clearAllSelections = useCallback(() => {
    setSelectionState({ ids: [], rowsById: new Map() });
  }, []);

  useEffect(() => {
    clearAllSelections();
  }, [filterParams, clearAllSelections]);

  const selectedRows = useMemo(
    (): TRow[] =>
      selectionState.ids
        .map(id => selectionState.rowsById.get(id))
        .filter((item): item is TRow => item !== undefined),
    [selectionState.ids, selectionState.rowsById],
  );

  const selectionIdSet = useMemo(
    () => new Set<GridRowId>(selectionState.ids),
    [selectionState.ids],
  );

  const visibleSelectedIds = useMemo(
    () => rows.filter(row => selectionIdSet.has(getRowId(row))).map(getRowId),
    [rows, selectionIdSet, getRowId],
  );

  return {
    selectedIds: selectionState.ids,
    handleRowSelectionChange,
    clearAllSelections,
    selectedRows,
    visibleSelectedIds,
  };
};
